import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFavouriteStops, useSavedTrips } from '@/store/favourites';
import { searchStops, type StopResult } from '@/services/stop-search';
import './HomeScreen.css';

const MODE_COLORS: Record<string, string> = {
  Train: '#F6891F', Bus: '#0072C6', Ferry: '#61B255',
  Metro: '#0099CD', 'Light Rail': '#D81E34',
};

const BADGE_LETTER: Record<string, string> = {
  Train: 'T', Bus: 'B', Ferry: 'F', Metro: 'M', 'Light Rail': 'L',
};

function formatCountdown(scheduled: string, predicted?: string, delayMin?: number): string {
  if (!scheduled) return '—';
  const target = predicted ? new Date(predicted) : new Date(scheduled);
  const now = new Date();
  const diff = Math.round((target.getTime() - now.getTime()) / 60000);
  if (diff <= -1) return `${Math.abs(diff)}m ago`;
  if (diff <= 0) return 'Now';
  if (diff < 60) return `${diff} min`;
  return new Date(scheduled).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

function StopSearchSheet({ onClose, favStops, onAdd }: {
  onClose: () => void;
  favStops: { id: string }[];
  onAdd: (stop: { id: string; name: string; parentName?: string }) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<StopResult[]>([]);
  const [loading, setLoading] = useState(false);

  const doSearch = useCallback(async (q: string) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const r = await searchStops(q);
      setResults(r);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  return (
    <div className="search-sheet-overlay" onClick={onClose}>
      <div className="search-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="search-sheet-header">
          <h2>Add Favourite Stop</h2>
          <button onClick={onClose} className="search-sheet-close">✕</button>
        </div>
        <input
          className="planner-input"
          placeholder="Search for a station or stop..."
          value={query}
          onChange={(e) => { setQuery(e.target.value); doSearch(e.target.value); }}
          autoFocus
        />
        {loading && <div className="search-sheet-loading">Searching...</div>}
        <div className="search-sheet-results">
          {results.map((r) => {
            const isFav = favStops.some((s) => s.id === r.id);
            return (
              <div key={r.id} className="search-sheet-item">
                <div className="search-sheet-item-info">
                  <span className="search-sheet-item-name">{r.name}</span>
                  {r.parentName && <span className="search-sheet-item-parent">{r.parentName}</span>}
                </div>
                <button
                  className={`search-sheet-add${isFav ? ' search-sheet-added' : ''}`}
                  onClick={() => { onAdd({ id: r.id, name: r.name, parentName: r.parentName }); }}
                  disabled={isFav}
                >
                  {isFav ? '✓ Added' : '+ Add'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function HomeScreen() {
  const navigate = useNavigate();
  const { stops, add, remove } = useFavouriteStops();
  const { trips, remove: removeTrip } = useSavedTrips();
  const [showSearch, setShowSearch] = useState(false);

  return (
    <main className="page-container">
      <div className="home-header">
        <h1 className="page-title">SydneyPT</h1>
        <button className="home-add-btn" onClick={() => setShowSearch(true)}>+ Add Stop</button>
      </div>

      <div className="home-content">
        <section className="card">
          <h2 className="card__title">Next Departures</h2>
          {stops.length === 0 ? (
            <div className="home-empty">
              <span className="home-empty-icon">🚉</span>
              <p className="home-empty-label">Add a favourite stop to see departures</p>
              <button className="home-empty-btn" onClick={() => setShowSearch(true)}>+ Add a stop</button>
            </div>
          ) : (
            <div className="home-departures">
              {stops.slice(0, 5).map((stop) => (
                <button key={stop.id} className="home-stop-group home-stop-card" onClick={() => navigate(`/stop/${stop.id}`)}>
                  <div className="home-stop-header">
                    <h3 className="home-stop-name">{stop.name}</h3>
                    <span className="home-stop-open">View services ›</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Saved trips */}
        <section className="card">
          <h2 className="card__title">Saved Trips</h2>
          {trips.length === 0 ? (
            <div className="home-empty">
              <span className="home-empty-icon">📌</span>
              <p className="home-empty-label">No saved trips yet</p>
              <p className="hint">Save a trip from the Plan tab</p>
            </div>
          ) : (
            trips.slice(0, 5).map((trip) => (
              <div key={trip.id} className="saved-trip-row">
                <button className="saved-trip-btn" onClick={() => navigate(`/plan?from=${trip.originId}&to=${trip.destId}&fromName=${encodeURIComponent(trip.originName)}&toName=${encodeURIComponent(trip.destName)}`)}>
                  <span className="saved-trip-name">{trip.name}</span>
                  <span className="saved-trip-route">{trip.originName} → {trip.destName}</span>
                </button>
                <button className="saved-trip-remove" onClick={() => removeTrip(trip.id)}>✕</button>
              </div>
            ))
          )}
        </section>
      </div>

      {showSearch && <StopSearchSheet onClose={() => setShowSearch(false)} favStops={stops} onAdd={(stop) => add(stop)} />}
    </main>
  );
}

